import Vue from "vue";
import axios from "axios";
import moment from "moment";
import jwt from "jsonwebtoken";
import { ADD_TOAST_MESSAGE } from "vuex-toast";
import webStorage from "@/libs/webStorage";

const baseUrl = process.env.VUE_APP_API_URL;
let REFRESHING_TOKEN = false;

const apiCall = async (
  dispatch,
  path,
  method,
  { base_url: base, headers, data, responseType }
) => {
  try {
    const basePath = base || baseUrl;
    const options = {
      url: `${basePath}${path}`,
      method,
      data,
      headers
    };

    if (responseType) {
      options.responseType = responseType;
    }
    const response = await axios(options);

    Vue.$log.debug(`API [${method}] ${path} success`, response);

    if (response.headers && response.headers["X-Timestamp"]) {
      var offset = parseInt(response.headers["X-Timestamp"]) - Date.now();
      moment.now = function() {
        return moment(Date.now() + offset);
      };
    }

    return response;
  } catch (error) {
    Vue.$log.debug(`API [${method}] ${path} error`, error);
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const response = error.response;
      if (response.status >= 500) {
        dispatch(
          ADD_TOAST_MESSAGE,
          { text: i18n.t('messages.try_again'), type: "danger" },
          { root: true }
        );
      }

      if (response.status == 429) {
        dispatch(
          ADD_TOAST_MESSAGE,
          {
            text: "Rate limit exceeded, please try in 5 more minutes",
            type: "danger"
          },
          { root: true }
        );
      }

      if (response.status == 401) {
        webStorage.remove("PRIVATE_TOKEN");
        webStorage.remove("REFRESH_TOKEN");
        webStorage.remove("USER_INFO");
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }

      return Promise.reject(response);
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      return Promise.reject(error.request);
    }
    return Promise.reject(error);
  }
};

const exportAPI = async (dispatch, { base_url, path, method = 'GET', data = null, headers = {} }) => {
  let token = webStorage.get("PRIVATE_TOKEN");

  if (!token) {
    webStorage.remove("PRIVATE_TOKEN");
    webStorage.remove("USER_INFO");
    setTimeout(() => {
      window.location.href = '/login';
    }, 1000);
    return;
  }

  // Check token expiration
  const info = jwt.decode(token);
  const issueAt = info.iat;
  const now = moment();
  const expiredBuffer = moment(issueAt, "X").add(20, "minutes");

  if (expiredBuffer < now) {
    token = await refreshToken(dispatch);
  }

  const fullHeaders = {
    ...headers,
    Authorization: `Bearer ${token}`,
  };

  const response = await apiCall(dispatch, path, method, {
    base_url,
    data,
    headers: fullHeaders,
    responseType: 'blob',
  });

  return response;
};

const privateApi = async (dispatch, { base_url, path, method, data, responseType }
) => {
  // check expire token
  let token = webStorage.get("PRIVATE_TOKEN");
  if (token) {
    const info = jwt.decode(token);
    const issueAt = info.iat;
    const now = moment();
    const expiredBuffer = moment(issueAt, "X").add(20, "minutes");

    if (expiredBuffer < now) {
      token = await refreshToken(dispatch);
    }

    const headers = { Authorization: 'Bearer ' + token };
    return apiCall(dispatch, path, method, {
      base_url,
      headers,
      data,
      responseType
    });
  } else {
    webStorage.remove("PRIVATE_TOKEN");
    webStorage.remove("USER_INFO");
    setTimeout(() => {
      window.location.href = '/login';
    }, 1000);
  }
};

const publicApi = async (
  dispatch,
  { base_url, path, method, data, responseType }
) => apiCall(dispatch, path, method, { base_url, data, responseType });

const refreshToken = async (dispatch, base_url) => {
  if (REFRESHING_TOKEN === true) {
    return waitForNewToken();
  }

  Vue.$log.info("Getting new token");
  const method = "post";
  const refreshToken = webStorage.get("REFRESH_TOKEN");
  const data = { "refreshToken": refreshToken };
  const response = await apiCall(dispatch, "/auth/refresh", method, {
    base_url,
    data
  });
  webStorage.set("PRIVATE_TOKEN", response.data.privateToken);
  REFRESHING_TOKEN = false;
  return response.data.privateToken;
};

const waitForNewToken = () => {
  let retrial = 0;
  return new Promise((resolve, reject) => {
    const interval = setInterval(function() {
      Vue.$log.info("Waiting for new token");
      if (!REFRESHING_TOKEN) {
        clearInterval(interval);
        const token = webStorage.get("PRIVATE_TOKEN");
        return resolve(token);
      }
      retrial++;
      if (retrial > 10) {
        clearInterval(interval);
        return reject(new Error("Cannot refresh token"));
      }
    }, 300);
  });
};
export default {
  exportAPI,
  privateApi,
  publicApi
};
